import React, { useState } from 'react';
import {
  Eye,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Lightbulb,
  Target,
  ListChecks,
} from 'lucide-react';
import { useVendorInsights } from '../../services/vendorAnalyticsService';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const PERIOD_TABS = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
];

const VendorInsights = () => {
  const [period, setPeriod] = useState('month');

  const { data, isLoading, error } = useVendorInsights(period);

  const insights = data?.data || {};

  const hasKeyInsights = insights.key_insights && insights.key_insights.length > 0;
  const hasTrends = insights.trends && insights.trends.length > 0;
  const hasRecommendations =
    insights.recommendations && insights.recommendations.length > 0;
  const isEmpty = !hasKeyInsights && !hasTrends && !hasRecommendations;

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Insights"
        subtitle="Actionable tips to grow your bookings and improve performance."
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.insights.title}
        steps={VENDOR_PAGE_GUIDES.insights.steps}
        icon={ListChecks}
      />

      {/* Period tabs */}
      <div
        className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
        role="tablist"
        aria-label="Insights period"
      >
        {PERIOD_TABS.map((tab) => {
          const isActive = period === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setPeriod(tab.value)}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                isActive
                  ? 'bg-white dark:bg-gray-900 shadow-sm text-gray-900 dark:text-gray-50'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-6 animate-pulse"
            >
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-300 px-4 py-3 text-sm flex items-start gap-2.5">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Failed to load insights. Please try again.</span>
        </div>
      ) : (
        <>
          {/* Key Insights */}
          {hasKeyInsights && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Key Insights
              </h2>
              <div className="space-y-3">
                {insights.key_insights.map((insight, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-950/40 border border-gray-100 dark:border-gray-800"
                  >
                    <Target className="w-5 h-5 text-sky-500 dark:text-sky-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700 dark:text-gray-300">{insight}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trends */}
          {hasTrends && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">
                Trends
              </h2>
              <div className="space-y-3">
                {insights.trends.map((trend, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {trend.direction === 'up' ? (
                        <TrendingUp className="w-5 h-5 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                      ) : (
                        <TrendingDown className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-gray-50 truncate">
                          {trend.metric}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {trend.description}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 border ${
                        trend.direction === 'up'
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50'
                          : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800/50'
                      }`}
                    >
                      {trend.change}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {hasRecommendations && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Recommendations
              </h2>
              <div className="space-y-3">
                {insights.recommendations.map((recommendation, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 rounded-xl bg-amber-50/70 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/40"
                  >
                    <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-50 mb-1">
                        {recommendation.title}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {recommendation.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {isEmpty && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-8 sm:p-12 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400">
                <Eye className="w-7 h-7" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
                No insights available
              </h3>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                No insights available for this period. Check back later for actionable insights.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VendorInsights;
